import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { usePagination } from './usePagination'

const PAGE_SIZES = [10, 20, 50, 100]

export type PagerProps = { total: number; page: number; size: number; onPage: (p: number) => void; onSize: (s: number) => void; className?: string }

/** Liste altı sayfalama: aralık bilgisi, önceki/sonraki ve sayfa boyutu. */
export function Pager({ total, page, size, onPage, onSize, className }: PagerProps) {
  const { t } = useTranslation()
  if (total <= PAGE_SIZES[0]!) return null
  const pages = Math.max(1, Math.ceil(total / size))
  return (
    <div className={cn('flex flex-wrap items-center gap-2 border-t px-4 py-3 text-sm', className)}>
      <span className="text-muted-foreground">{t('common.list.range', { total, from: total ? (page - 1) * size + 1 : 0, to: Math.min(page * size, total) })}</span>
      <div className="ml-auto flex items-center gap-1">
        <Button size="icon" variant="outline" className="size-8" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t('common.list.prev')}>
          <ChevronLeft />
        </Button>
        <span className="px-2 tabular-nums">{t('common.list.page', { page, pages })}</span>
        <Button size="icon" variant="outline" className="size-8" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t('common.list.next')}>
          <ChevronRight />
        </Button>
        <Select size="sm" className="w-28" value={String(size)} onChange={(v) => onSize(Number(v))} aria-label={t('common.list.pageSize')} options={PAGE_SIZES.map((n) => ({ value: String(n), label: t('common.list.perPage', { count: n }) }))} />
      </div>
    </div>
  )
}

export function Paged<T>({ rows, resetKey, children }: { rows: readonly T[]; resetKey?: unknown; children: (pageRows: T[]) => ReactNode }) {
  const { pageRows, pager } = usePagination(rows, resetKey)
  return (
    <>
      {children(pageRows)}
      <Pager {...pager} />
    </>
  )
}
